'use client';

/**
 * Featured Events Carousel
 *
 * Auto-rotating elegant carousel for featured VIP events
 */

import { useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import Link from 'next/link';
import { Event } from '@/lib/mock-data';
import { Calendar, MapPin, ChevronLeft, ChevronRight, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FeaturedCarouselProps {
  events: Event[];
}

export function FeaturedCarousel({ events }: FeaturedCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 30 });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const getLowestPrice = (event: Event) => {
    if (event.packages.length === 0) return null;
    const prices = event.packages.map(pkg => pkg.priceUSDT);
    return Math.min(...prices);
  };

  if (events.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full">
      {/* Carousel Container */}
      <div className="overflow-hidden rounded-xl" ref={emblaRef}>
        <div className="flex">
          {events.map((event) => {
            const lowestPrice = getLowestPrice(event);

            return (
              <div key={event.id} className="flex-[0_0_100%] min-w-0">
                <div className="relative aspect-[21/9] md:aspect-[16/7] w-full">
                  {/* Background Image */}
                  <Image
                    src={event.imageUrl}
                    alt={event.eventTitle}
                    fill
                    className="object-cover"
                    priority
                  />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent" />

                  {/* Content */}
                  <div className="absolute inset-0 flex items-center">
                    <div className="container mx-auto px-6 md:px-12">
                      <div className="max-w-2xl space-y-6 animate-in slide-in-from-left-10 duration-700">
                        {/* Category Badge */}
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 backdrop-blur-sm border border-primary/30">
                          <Ticket className="h-4 w-4 text-primary" />
                          <span className="text-sm font-medium text-primary">Featured VIP Event</span>
                        </div>

                        {/* Artist Name */}
                        <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
                          {event.artistName}
                        </h2>

                        {/* Event Title */}
                        <p className="text-xl md:text-2xl text-white/90 font-medium">
                          {event.eventTitle}
                        </p>

                        {/* Event Details */}
                        <div className="flex flex-wrap gap-6 text-white/80">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-primary" />
                            <span className="text-base md:text-lg">{formatDate(event.date)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-5 w-5 text-primary" />
                            <span className="text-base md:text-lg">{event.city}, {event.country}</span>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-base md:text-lg text-white/70 max-w-xl line-clamp-2">
                          {event.description}
                        </p>

                        {/* Price and CTA */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-4">
                          {lowestPrice && (
                            <div className="flex items-baseline gap-2">
                              <span className="text-sm text-white/60">From</span>
                              <span className="text-3xl md:text-4xl font-bold text-primary">
                                {lowestPrice.toLocaleString()}
                              </span>
                              <span className="text-lg text-primary">USDT</span>
                            </div>
                          )}

                          <Button
                            size="lg"
                            className="bg-gradient-to-r from-primary to-secondary hover:shadow-gold text-base md:text-lg px-8"
                            asChild
                          >
                            <Link href={`/events/${event.id}`}>
                              Book Your VIP Package
                            </Link>
                          </Button>
                        </div>

                        {/* Limited Tickets Warning */}
                        {event.ticketsRemaining < 10 && (
                          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-urgency/20 backdrop-blur-sm border border-urgency">
                            <span className="text-sm font-bold text-urgency pulse-urgency">
                              ⚡ Only {event.ticketsRemaining} VIP tickets remaining!
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Buttons */}
      {events.length > 1 && (
        <>
          <button
            onClick={scrollPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 backdrop-blur-sm border border-primary/30 text-white hover:bg-primary hover:border-primary transition-all duration-300 hover:scale-110"
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          <button
            onClick={scrollNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 backdrop-blur-sm border border-primary/30 text-white hover:bg-primary hover:border-primary transition-all duration-300 hover:scale-110"
            aria-label="Next slide"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {events.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {events.map((_, index) => (
            <button
              key={index}
              onClick={() => emblaApi?.scrollTo(index)}
              className="w-2 h-2 rounded-full bg-white/40 hover:bg-primary transition-all duration-300"
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
